/**
 * Datadog Web Vitals Tracking Component
 *
 * This component uses Next.js's useReportWebVitals hook to capture Core Web Vitals
 * and other performance metrics, then reports them to Datadog RUM.
 *
 * Core Web Vitals Tracked:
 * - LCP (Largest Contentful Paint): Loading performance
 * - FID (First Input Delay): Interactivity (deprecated, replaced by INP)
 * - CLS (Cumulative Layout Shift): Visual stability
 * - INP (Interaction to Next Paint): Interactivity (new metric)
 * - TTFB (Time to First Byte): Server response time
 * - FCP (First Contentful Paint): Initial render time
 *
 * Usage:
 * Import and render this component in _app.tsx alongside DatadogRUM component.
 */

import { datadogRum } from "@datadog/browser-rum";
import { useReportWebVitals } from "next/web-vitals";
import { useRef } from "react";
import { EnvFactory } from "@shared/env/EnvFactory";

/**
 * Metric interface matching Next.js web vitals format
 */
interface WebVitalsMetric {
  id: string;
  name: string;
  value: number;
  label: "web-vital" | "custom";
  startTime?: number;
  rating?: "good" | "needs-improvement" | "poor";
}

/**
 * DatadogWebVitals component captures and reports web vitals to Datadog
 */
const DatadogWebVitals: React.FC = () => {
  const reportedMetrics = useRef<Set<string>>(new Set());

  /**
   * Report web vitals to Datadog RUM
   */
  useReportWebVitals((metric: WebVitalsMetric) => {
    // Only run on client-side and when RUM is enabled
    if (typeof window === "undefined") {
      return;
    }

    const isRumEnabled = EnvFactory.getValue("DD_RUM_ENABLED") === "true";
    if (!isRumEnabled) {
      return;
    }

    // Prevent duplicate reporting of the same metric
    const metricKey = `${metric.name}-${metric.id}`;
    if (reportedMetrics.current.has(metricKey)) {
      return;
    }
    reportedMetrics.current.add(metricKey);

    try {
      // Report metric as a custom timing
      datadogRum.addTiming(
        `web_vital_${metric.name.toLowerCase()}`,
        metric.value
      );

      // Also report as an action with additional context
      datadogRum.addAction(`web_vital_${metric.name}`, {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_id: metric.id,
        metric_label: metric.label,
        metric_rating: metric.rating,
        start_time: metric.startTime,
        // Add current page context
        page_path: window.location.pathname,
        page_url: window.location.href,
      });

      // Categorize metrics for better analysis
      const metricCategory = getMetricCategory(metric.name);
      const performanceThreshold = getPerformanceThreshold(metric.name);

      // Add custom user action if metric exceeds threshold
      if (performanceThreshold && metric.value > performanceThreshold.poor) {
        datadogRum.addAction(`poor_performance_${metric.name.toLowerCase()}`, {
          metric_name: metric.name,
          metric_value: metric.value,
          threshold: performanceThreshold.poor,
          category: metricCategory,
          rating: "poor",
        });
      }

      // Log in development for debugging
      if (EnvFactory.getValue("DD_ENV") === "preview") {
        // eslint-disable-next-line no-console
        console.info(`[Web Vitals] ${metric.name}:`, {
          value: metric.value,
          rating: metric.rating,
          id: metric.id,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to report web vital to Datadog:", error);
    }
  });

  // This component doesn't render anything
  return null;
};

/**
 * Get the category for a web vital metric
 */
function getMetricCategory(metricName: string): string {
  const categories: Record<string, string> = {
    LCP: "loading",
    FID: "interactivity",
    INP: "interactivity",
    CLS: "visual_stability",
    TTFB: "server_response",
    FCP: "loading",
  };
  return categories[metricName] || "other";
}

/**
 * Get performance thresholds for each metric
 * Based on Google's Core Web Vitals recommendations
 */
function getPerformanceThreshold(
  metricName: string
): { good: number; needsImprovement: number; poor: number } | null {
  const thresholds: Record<
    string,
    { good: number; needsImprovement: number; poor: number }
  > = {
    // LCP thresholds (milliseconds)
    LCP: {
      good: 2500,
      needsImprovement: 4000,
      poor: 4000,
    },
    // FID thresholds (milliseconds) - deprecated but still tracked
    FID: {
      good: 100,
      needsImprovement: 300,
      poor: 300,
    },
    // INP thresholds (milliseconds)
    INP: {
      good: 200,
      needsImprovement: 500,
      poor: 500,
    },
    // CLS thresholds (score, unitless)
    CLS: {
      good: 0.1,
      needsImprovement: 0.25,
      poor: 0.25,
    },
    // TTFB thresholds (milliseconds)
    TTFB: {
      good: 800,
      needsImprovement: 1800,
      poor: 1800,
    },
    // FCP thresholds (milliseconds)
    FCP: {
      good: 1800,
      needsImprovement: 3000,
      poor: 3000,
    },
  };

  return thresholds[metricName] || null;
}

export default DatadogWebVitals;
